import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath } from 'react-router-dom';
import get from 'lodash/get';
import Header from './components/Header';
import Tabs from './components/Tabs';
import {
  Wrapper,
  DetailWrapper,
  ResizerWrapper,
  Resizer,
  Transparency,
  ModalWrapper
} from './styled';
import { useResizeX } from 'Hooks/useResize';
import useWindowDimensions from 'Hooks/useWindowSize';
import useShortcutScope from 'Hooks/useShortcutScope';
import useFormShortcuts from 'Hooks/useFormShortcuts';
import ImagePreviewModal from './components/ImagePreviewModal';
import CreateEditTagsModal from './components/CreateEditTagsModal';
import {
  selectShowDetail,
  selectVulnDetailId,
  selectVulnDetailTags,
  selectVulnsSelected
} from 'store/Manage/selectors';
import { hideVulnerabilityDetail, redirectToManage } from 'store/Manage/actions';
import { selectPathname } from 'store/Router/selectors';
import { selectShowTagsModal } from 'store/Tags/selectors';
import IntegrationsModals from './components/IntegrationsModals';
import TabSelector from './components/Tabs/components/TabSelector';
import Warning from 'Common/Components/Warning';
import { useIntl } from 'react-intl';
import { resetWarning } from 'store/Settings/settingsActions';
import DeleteConfirmationModal from 'Screens/Credentials/components/ActionBar/components/DeleteConfirmationModal';
import { selectDeleteConfirmation } from 'store/Credentials/selectors';

const VulnerabilityDetail = () => {
  const [showTransparency, setShowTransparency] = useState(false);
  const ref = useRef(null);
  const intl = useIntl();
  const wrapperRef = useRef(null);
  const resizerRef = useRef(null);
  const { width } = useWindowDimensions();
  const minWidth = width < 3000 ? width * 0.60 : width * 0.35;
  const maxWidth = width < 3000 ? width * 0.75 : width * 0.55;
  const wrapperWidth = useResizeX(
    wrapperRef,
    resizerRef,
    minWidth,
    minWidth,
    maxWidth
  );
  const showCreateTagModal = useSelector((state) =>
    selectShowTagsModal('vulnDetail', state)
  );
  const vulnsTags = useSelector(selectVulnDetailTags);
  const error = useSelector((state) =>
    get(state, 'settings.sendVulnToTool.payload.error', false)
  );
  const message = useSelector((state) =>
    get(state, 'settings.sendVulnToTool.payload.message', '')
  );
  const dispatch = useDispatch();
  const isVulnDetailOpen = useSelector(selectShowDetail);
  const vulnDetailId = useSelector(selectVulnDetailId);
  const vulnsSelected = useSelector(selectVulnsSelected);
  const showDeleteCredentialModal = useSelector(selectDeleteConfirmation);
  const pathname = useSelector(selectPathname);
  const shortcutScope = useShortcutScope('vuln-detail');

  const isExploreRoute = Boolean(matchPath(pathname, {
    path: '/vulns/:ws/groups/:groupId',
    exact: true,
    strict: false
  }));

  const handleClose = () => {
    if (isExploreRoute) {
      dispatch(hideVulnerabilityDetail());
      return;
    }
    dispatch(redirectToManage());
  };

  useFormShortcuts({ onCancel: handleClose, scope: shortcutScope, cancelLabel: 'Close' });

  const onScroll = () => {
    const scrollTop = get(ref, 'current.scrollTop', 0);
    if (scrollTop === 0) setShowTransparency(false);
    else setShowTransparency(true);
  };

  return (
    <>
      { showDeleteCredentialModal && <DeleteConfirmationModal isVulnDetail /> }
      <ImagePreviewModal />
      {showCreateTagModal && (
        <CreateEditTagsModal vulnsTags={ vulnsTags } entity="vulnDetail" />
      )}
      <IntegrationsModals
        vulnsSelected={ vulnsSelected }
        vulnId={ vulnDetailId }
        isVulnDetailOpen={ isVulnDetailOpen }
        entity="vulns"
      />
      {error && message && (
        <ModalWrapper>
          <Warning
            title={ intl.formatMessage({ id: 'sendToTool.warnings.oops.title' }) }
            onClose={ () => dispatch(resetWarning()) }
          >
            {message}
          </Warning>
        </ModalWrapper>
      )}
      <Wrapper ref={ wrapperRef } wrapperWidth={ wrapperWidth }>
        <ResizerWrapper ref={ resizerRef } children={ <Resizer /> } />
        <DetailWrapper>
          <Header onClose={ handleClose } />
          <TabSelector scope={ shortcutScope } />
          {showTransparency && <Transparency />}
          <Tabs
            tabRef={ ref }
            onScroll={ onScroll }
            showTransparency={ showTransparency }
          />
        </DetailWrapper>
      </Wrapper>
    </>
  );
};

export default VulnerabilityDetail;
